import { round } from 'lodash-es';
import { useIntl } from 'react-intl';
import { getMinutesFromNow } from '#/shared/utils/date-time';
import { FEET_IN_A_MILE } from '#/shared/constants';

const DUE_THRESHOLD_MINUTES = 1;
const DUE_THRESHOLD_FEET = 250;

const useEstimatedArrivalText = () => {
  const intl = useIntl();

  /**
   * Returns text describing the amount of time or distance the vehicle to which
   * the arrival pertains is away from the target stop
   *
   * @param {object} arrival
   * @param {boolean} arrival.showMilesAway If `true` arrival message describes
   * the vehicle's distance from the stop otherwise the message is in terms of
   * the time until the vehicle arrives
   * @param {number} arrival.estimatedTime Vehicle's estimated arrival time in
   * milliseconds since the epoch, can also be passed in on the `arrival` object
   * under the key `estimated`
   * @param {number} arrival.feet Vehicle's approximate distance from the stop
   *
   * @param {object} [options]
   * @param {boolean} [options.includePredicate] If `true` predicate text is
   * prepended to the arrival time message
   * @param {boolean} [options.useLongFormUnits] If a `true` a long fom version
   * of the arrival time unit is used in the message
   *
   * @returns {{arrivalText: string, arrivalDue: boolean}}
   */
  const getEstimatedArrivalText = (
    arrival,
    { includePredicate = false, useLongFormUnits = false } = {}
  ) => {
    let arrivalText = '';
    let arrivalDue = false;

    if (arrival) {
      let intlId;
      let intlValues;
      const {
        showMilesAway,
        estimated,
        estimatedTime,
        feet: feetFromStop,
      } = arrival;

      // this accommodates inconsistencies in how the app structures
      // arrival objects
      const arrivalTime = estimated || estimatedTime;

      if (!showMilesAway && typeof arrivalTime === 'number') {
        const minutesUntilArrival = getMinutesFromNow(arrivalTime);

        if (minutesUntilArrival < DUE_THRESHOLD_MINUTES) {
          arrivalDue = true;
          intlId = 'due';
        } else {
          const messageParts = [];
          if (includePredicate) {
            messageParts.push(
              `${intl.formatMessage({ id: 'arrivals.arriving-in' })} `
            );
          }

          let minutesTextId = 'minutes-short';
          if (useLongFormUnits) {
            minutesTextId = minutesUntilArrival === 1 ? 'minute' : 'minutes';
          }

          messageParts.push(
            `${minutesUntilArrival} ${intl.formatMessage({
              id: `arrivals.${minutesTextId}`,
            })}`
          );
          arrivalText = messageParts.join('');
        }
      } else if (showMilesAway && typeof feetFromStop === 'number') {
        if (feetFromStop < DUE_THRESHOLD_FEET) {
          arrivalDue = true;
          intlId = 'due';
        } else {
          const miles = round(feetFromStop / FEET_IN_A_MILE, 1);
          intlId = miles === 1 ? 'mile-away' : 'miles-away';
          intlValues = { miles };
        }
      }

      if (arrivalDue) {
        arrivalText = intl.formatMessage({ id: `arrivals.${intlId}` });
      } else if (intlId && !arrivalDue) {
        arrivalText = intl.formatMessage(
          { id: `arrivals.${intlId}` },
          intlValues
        );
      }
    }

    return {
      arrivalText,
      arrivalDue,
    };
  };

  return {
    getEstimatedArrivalText,
  };
};

export default useEstimatedArrivalText;
